import React, { useCallback, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'

import { appApi } from '../../api/app.api'
import { formatDate } from '../../utils/helper.util'
import { modal } from '../../utils/antd.util'
import { rateLimitsByHeader } from '../../utils/weight.util'
import { round } from '../../utils/calc.util'

function Account () {
  const [accountInfo, setAccountInfo] = useState({})
  const [logs, setLogs] = useState([])
  const [rateLimits] = useState(Object.keys(rateLimitsByHeader))

  const isLoading = useRef(false)
  const ws = useRef(null)

  useEffect(() => {
    const logsHandler = (event) => {
      if (event.detail.log) {
        setLogs(logs => {
          return [event.detail.log, ...logs]
        })
      }
    }
    window.addEventListener('apiLogs', logsHandler)

    return () => {
      window.removeEventListener('apiLogs', logsHandler)
    }

  }, [])

  const getAccountInfo = useCallback(() => {
    isLoading.current = true

    appApi.getAccountInfo().then(response => {
      setAccountInfo(response.data)
    })
      .finally(() => {
        isLoading.current = false
      })

  }, [])

  useEffect(() => {
    if (!ws.current) {
      const webSocket = new WebSocket(import.meta.env.VITE_WS_URL)
      ws.current = webSocket


      webSocket.onopen = function () {
        getAccountInfo()
      }

      webSocket.onmessage = (response) => {
        const wsResponse = response.data.toString()

        if (wsResponse === 'live-updated') {
          getAccountInfo()
        }
      }
    }
  }, [])

  const closeOrderByMarket = async (order) => {
    modal.confirm({
      title: 'Are you sure?',
      content: 'Order will be canceled and closed by the market price',
      okText: 'OK',
      cancelText: 'Cancel',
      okButtonProps: { type: 'primary', danger: true },
      cancelButtonProps: { type: 'default' },
      async onOk () {
        try {
          // await appApi.closeOrderByMarket(order.symbol, order.orderId);
          console.log('closeOrderByMarket')
        } catch (error) {
          console.error(
            'closeOrderByMarket',
            error.response?.data?.message ?? error
          )
        }
      }
    })
  }

  const openOrderByRates = async (symbol) => {
    modal.confirm({
      title: 'Are you sure?',
      content: 'Order will be placed by the symbol rates',
      okText: 'OK',
      cancelText: 'Cancel',
      okButtonProps: { type: 'primary', danger: true },
      cancelButtonProps: { type: 'default' },
      async onOk () {
        try {
          // await appApi.openOrderByRates(symbol);
          console.log('openOrderByRates')
        } catch (error) {
          console.error(
            'openOrderByRates',
            error.response?.data?.message ?? error
          )
        }
      }
    })
  }

  return (
    <div className="cards">
      <div className="card">
        <div className="card-header">
          <div className="card-row">
            <div className="card-cell card-cell_3">Account balance</div>
            <div className="card-cell card-cell_1">Price</div>
            <div className="card-cell card-cell_1">Locked</div>
            <div className="card-cell card-cell_1">Free</div>
            <div className="card-cell card-cell_1">Total</div>
            <div className="card-cell card-cell_1">%</div>
          </div>
        </div>
        <div className="card-content">
          <div className="card-row">
            <span className="card-cell card-cell_3">All coins</span>
            <span className="card-cell card-cell_1">-</span>
            <span className="card-cell card-cell_1">-</span>
            <span className="card-cell card-cell_1">-</span>
            <span className="card-cell card-cell_1">{round(accountInfo.account?.spot?.balance) || 0}</span>
            <span className="card-cell card-cell_1">-</span>
          </div>
          {accountInfo.account?.spot?.coins && Object.keys(accountInfo.account?.spot?.coins)
            .filter(coinSymbol => accountInfo.account?.spot?.coins[coinSymbol].totalPrice > 1)
            .sort((coinSymbol1, coinSymbol2) => {
              const totalPrice1 = accountInfo.account?.spot?.coins[coinSymbol1].totalPrice
              const totalPrice2 = accountInfo.account?.spot?.coins[coinSymbol2].totalPrice

              return totalPrice2 - totalPrice1
            })
            .map(coinSymbol => {
              const coinData = accountInfo.account?.spot?.coins[coinSymbol]

              return (
                <div key={coinSymbol} className="card-row">
                  <span className="card-cell card-cell_3">{coinSymbol}</span>
                  <span className="card-cell card-cell_1">
                    {round(coinData.price, 4)}
                  </span>
                  <span className="card-cell card-cell_1">
                    {round(coinData.locked * coinData.price)}
                  </span>
                  <span className="card-cell context-menu-cursor card-cell_1"
                        onClick={() => openOrderByRates(coinSymbol)}>
                    {round(coinData.free * coinData.price)}
                  </span>
                  <span className="card-cell card-cell_1">
                    {round(coinData.totalPrice)}
                  </span>
                  <span className="card-cell card-cell_1">
                    {round(coinData.balancePercentage ?? 0)}
                  </span>
                </div>
              )
            })}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-row">
            <span className="card-cell card-cell_1">Weight</span>
          </div>
        </div>
        <div className="card-content">
          {accountInfo.account?.limits && rateLimits.map((weightName) => (
              <div key={weightName} className="card-row">
                <span className="card-cell card-cell_2">{weightName}</span>
                <span className="card-cell card-cell_1">
                  {accountInfo.account?.limits[weightName] || 0}/{rateLimitsByHeader[weightName]}
                </span>
              </div>
            )
          )}
        </div>
      </div>
      <div className="card card-full-width">
        <div className="card-header">Logs</div>
        <div className="card-content">
          {logs.map((log, index) => (
            <div className={classNames('card-row', log.type === 'response' ? 'card-row-success' : 'card-row-danger')}
                 key={log.date + index}>
              {`${formatDate(log.date)} - ${log.status} ${log.url} ${log.statusText} ${log.message}`}
            </div>
          ))}
        </div>
      </div>
      <div className="card card-full-width">
        <div className="card-header pointer-cursor" onClick={() => getAccountInfo()}>
          <div className="card-row">
            <div className="card-cell card-cell_1">Symbol</div>
            <div className="card-cell card-cell_1">Side</div>
            <div className="card-cell card-cell_1">Type</div>
            <div className="card-cell card-cell_1">Qty</div>
            <div className="card-cell card-cell_1">Buy Price</div>
            <div className="card-cell card-cell_1">Sell Price</div>
            <div className="card-cell card-cell_1">Stop price</div>
            <div className="card-cell card-cell_1">Current price</div>
            <div className="card-cell card-cell_2">Time</div>
            <div className="card-cell card-cell_1">%</div>
          </div>
        </div>
        <div className="card-content">
          {accountInfo.account?.spot?.calculatedOpenOrders.map(order => {
            const profitClassnames = classNames(
              'card-cell',
              'card-cell_1',
              'context-menu-cursor',
              order.ui?.limitProgress > 80 ? 'card-cell-gold' :
                order.ui?.limitProgress > 0 ? 'card-cell-success' : 'card-cell-danger'
            )
            const price = round(order.price, 4)

            return (
              <div key={order.orderId} className="card-row">
                <span className="card-cell card-cell_1">{order.symbol}</span>
                <span className="card-cell card-cell_1">{order.side}</span>
                <span className="card-cell card-cell_1">{order.type}</span>
                <span className="card-cell card-cell_1">{round(order.origQty)}</span>
                <span className="card-cell card-cell_1">{order.side === 'SELL' ? order.ui?.buyingPrice : price}</span>
                <span className="card-cell card-cell_1">{price}</span>
                <span className="card-cell card-cell_1">{order.ui?.stopPrice}</span>
                <span className="card-cell card-cell_1">{order.ui?.currentPrice}</span>
                <span className="card-cell card-cell_2">{(new Date(order.time)).toLocaleString()}</span>
                <span className={profitClassnames} onClick={() => closeOrderByMarket(order)}>
                  {isFinite(order.ui?.limitProgress) && order.ui.limitProgress}
                </span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default Account
